import { Button, Grid } from '@material-ui/core';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityAboutCard,
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSubcomponentsCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isComponentType,
  isKind,
  hasCatalogProcessingErrors,
  isOrphan,
  hasRelationWarnings,
  EntityRelationWarning,
  EntityDependencyOfComponentsCard,
} from '@backstage/plugin-catalog';
import {
  EntityUserProfileCard,
  EntityGroupProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
} from '@backstage/plugin-org';
import { EntityTechdocsContent, isTechDocsAvailable } from '@backstage/plugin-techdocs';
import { EmptyState } from '@backstage/core-components';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';

import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { EntityKubernetesContent } from '@backstage/plugin-kubernetes';
import { EntitySonarQubeCard } from '@backstage-community/plugin-sonarqube'
import { ChartSummaryCard, DeploymentGateCard } from '@internal/plugin-dev-services-chart-summary';
import { isAdrAvailable, EntityAdrContent } from '@backstage-community/plugin-adr';
import { BrightlyAdrFilter } from '@internal/adr-utils-common';
import { EntityCicdScansPage, EntitySecurityScanPage } from '@internal/backstage-plugin-wiz-plugin';
import { EntityGrafanaDashboardsCard } from '@backstage-community/plugin-grafana';

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid item xs={12}>
          <EntityRelationWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}

    {/* Row 1 - Show about and sonarcloud if applicable, stretch out about otherwise */}
    <EntitySwitch>
      <EntitySwitch.Case if={isComponentType(['cdn', 'service', 'library'])}>
        <Grid item md={6}>
          <EntityAboutCard variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntitySonarQubeCard variant="gridItem" />
        </Grid>
        <Grid item md={12}>
          <EntityCicdScansPage />
        </Grid>
        <EntitySwitch>
          <EntitySwitch.Case if={isComponentType(['service'])}>
            <Grid item md={6}>
              <EntityGrafanaDashboardsCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
      </EntitySwitch.Case>
      <EntitySwitch.Case>
        <Grid item md={12}>
          <EntityAboutCard variant="gridItem" />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    {/* Row 2 - Show charts deployments, and relations if applicable, stretch out relations otherwise */}
    <EntitySwitch>
      <EntitySwitch.Case if={isComponentType(['cdn', 'service'])}>
        <Grid item md={6}>
          <ChartSummaryCard />
        </Grid>
        <Grid item md={6}>
          <DeploymentGateCard />
        </Grid>
        <Grid item md={12} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
      </EntitySwitch.Case>
      <EntitySwitch.Case if={isComponentType(['library', 'chart'])}>
        <Grid item md={6}>
          <ChartSummaryCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
      </EntitySwitch.Case>
      <EntitySwitch.Case>
        <Grid item md={12} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    {/* Row 3 - Stretch out subcomponents card for all types other than documentation */}
    <EntitySwitch>
      <EntitySwitch.Case if={isComponentType(['documentation'])}>
        <Grid />
      </EntitySwitch.Case>
      <EntitySwitch.Case>
        <Grid item md={12} xs={12}>
          <EntityHasSubcomponentsCard variant="gridItem" />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </Grid>
);

const repositoryOverviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}
    <EntitySwitch>
      <EntitySwitch.Case if={isComponentType(['repository'])}>
        <Grid item md={6}>
          <EntityAboutCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <ChartSummaryCard />
        </Grid>
      </EntitySwitch.Case>
      <EntitySwitch.Case>
        <Grid item md={12}>
          <EntityAboutCard variant="gridItem" />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
    <Grid item md={12} xs={12}>
      <EntityCatalogGraphCard variant="gridItem" height={400} />
    </Grid>
    <Grid item md={12} xs={12}>
      <EntityDependencyOfComponentsCard variant="gridItem" />
    </Grid>
  </Grid>
);

const addOverviewPage = (content: React.JSX.Element) => {
  return (
    <EntityLayout.Route path="/" title="Overview">
      {content}
    </EntityLayout.Route>
  )
}

const addCicdPage = () => (
  <EntityLayout.Route path="/ci-cd" title="CI/CD">
    <EntitySwitch>

      {/* 
      Tekton is removed pending https://github.com/janus-idp/backstage-plugins/issues/1286
      as it screws up the styling of markdown content / adrs
      <EntitySwitch.Case if={isTektonCIAvailable}>
        <TektonCI />
      </EntitySwitch.Case> */
      }

      <EntitySwitch.Case>
        <EmptyState
          title="No CI/CD available for this entity"
          missing="info"
          description="You need to add an annotation to your component if you want to enable CI/CD for it. You can read more about annotations in Backstage by clicking the button below."
          action={
            <Button
              variant="contained"
              color="primary"
              href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
            >
              Read more
            </Button>
          }
        />
      </EntitySwitch.Case>
    </EntitySwitch>
  </EntityLayout.Route>
)

const addKubernetesPage = () => (
  <EntityLayout.Route path="/kubernetes" title="Kubernetes">
    <EntityKubernetesContent refreshIntervalMs={30000} />
  </EntityLayout.Route>
)

const addApiPage = () => (
  <EntityLayout.Route path="/api" title="API">
    <Grid container spacing={3} alignItems="stretch" direction='column'>
      <Grid item xs>
        <EntityProvidedApisCard />
      </Grid>
      <Grid item xs>
        <EntityConsumedApisCard />
      </Grid>
    </Grid>
  </EntityLayout.Route>
)

const addDependenciesPage = () => (
  <EntityLayout.Route path="/dependencies" title="Dependencies">
    <Grid container spacing={3} alignItems="stretch">
      <Grid item md={6}>
        <EntityDependsOnComponentsCard variant="gridItem" />
      </Grid>
      <Grid item md={6}>
        <EntityDependsOnResourcesCard variant="gridItem" />
      </Grid>
    </Grid>
  </EntityLayout.Route>
)

const addDocsPage = () => (
  <EntityLayout.Route if={isTechDocsAvailable} path="/docs" title="Docs">
    <EntityTechdocsContent>
      <TechDocsAddons>
        <ReportIssue />
        <Mermaid config={{ themeVariables: { lineColor: 'currentColor' } }} />
      </TechDocsAddons>
    </EntityTechdocsContent>
  </EntityLayout.Route>
)

const addAdrsPage = () => (
  <EntityLayout.Route if={isAdrAvailable} path="/adrs" title="ADRs">
    <EntityAdrContent filePathFilterFn={BrightlyAdrFilter} />
  </EntityLayout.Route>
)

const addWizScanPage = () => (
  <EntityLayout.Route path="/wiz-scan" title="Wiz Scan">
    <EntitySecurityScanPage />
  </EntityLayout.Route>
);

const addPages = (pages: {
  overviewContent?: React.JSX.Element,
  cicd?: boolean,
  kubernetes?: boolean,
  api?: boolean,
  dependencies?: boolean,
  docs?: boolean,
  adr?: boolean,
  wizScan?: boolean,
}) => {
  const {
    overviewContent,
    cicd,
    kubernetes,
    api,
    dependencies,
    docs,
    adr,
    wizScan
  } = pages;

  return (
    <EntityLayout>
      {overviewContent && addOverviewPage(overviewContent)}
      {cicd && addCicdPage()}
      {kubernetes && addKubernetesPage()}
      {api && addApiPage()}
      {dependencies && addDependenciesPage()}
      {docs && addDocsPage()}
      {adr && addAdrsPage()}
      {wizScan && addWizScanPage()}
    </EntityLayout>
  );
};

const createComponentPage = () => {
  const commonPages = { overviewContent, docs: true, adr: true };
  return (
    <EntitySwitch>
      <EntitySwitch.Case if={isComponentType('gitops')} children={addPages({ overviewContent: repositoryOverviewContent, docs: true })} />
      <EntitySwitch.Case if={isComponentType('repository')} children={addPages({ overviewContent: repositoryOverviewContent })} />
      <EntitySwitch.Case if={isComponentType('service')} children={addPages({ ...commonPages, kubernetes: true, api: true, dependencies: true, wizScan: true })} />
      <EntitySwitch.Case if={isComponentType(['cdn', 'library', 'chart'])} children={addPages({ ...commonPages, dependencies: true })} />
      <EntitySwitch.Case>{addPages(commonPages)}</EntitySwitch.Case>
    </EntitySwitch>
  );
};

const apiPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid container item md={12}>
          <Grid item md={6}>
            <EntityProvidingComponentsCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumingComponentsCard />
          </Grid>
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityGroupProfileCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard variant="gridItem" />
        </Grid>
        <Grid item xs={12}>
          <EntityMembersListCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const systemPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item md={8}>
          <EntityHasComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityHasApisCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityHasResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        variant="gridItem"
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    </EntityLayout.Route>
  </EntityLayout>
);

const domainPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
        <Grid item md={6}>
          <EntityHasSystemsCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />
    <EntitySwitch.Case>{createComponentPage()}</EntitySwitch.Case>
  </EntitySwitch>
);
